import React from 'react'
import PropTypes from 'prop-types'
import { Input } from 'antd'
import { classNames } from 'utils/utils'
import strings from 'utils/strings'
import InputLabel from 'components/patterns/Inputs/InputLabel'

import 'styles/AntOverrides.css'
import 'styles/ChecComponents.css'
import 'styles/InputBox.css'

// Wraps the antd input component as an input box. Must be a class in order to
// keep track of the number of chars remaining when a maxLength is set.
export default class InputBox extends React.PureComponent {
  constructor(props) {
    super(props)

    const disallowedRegEx = props.disallowedCharacters
      ? new RegExp(`[${props.disallowedCharacters.join('')}]`)
      : undefined
    const charsRemaining = props.value
      ? props.maxLength - props.value.length
      : props.maxLength
    const regExViolation =
      props.value && disallowedRegEx
        ? props.value.match(disallowedRegEx)
        : false
    const isMissingRequiredValue = props.required && !(props.value || '').trim()
    const exceedsMaxLength = props.maxLength > -1 && charsRemaining < 0
    const isValid = !(
      exceedsMaxLength ||
      regExViolation ||
      isMissingRequiredValue
    )

    this.state = {
      charsRemaining,
      disallowedRegEx,
      regExViolation,
      isMissingRequiredValue,
      isValid,
    }
  }

  componentDidMount = () => {
    if (!this.state.isValid) {
      this.props.onValidityChange(this.state.isValid, this.props.name)
    }
  }

  handleChange = (event) => {
    const { maxLength, required, onValidityChange } = this.props
    const { disallowedRegEx } = this.state

    const len = event.target.value.length
    const charsRemaining = maxLength - len

    const regExViolation = disallowedRegEx
      ? event.target.value.match(disallowedRegEx)
      : false
    const isMissingRequiredValue =
      required && !(event.target.value || '').trim()
    const exceedsMaxLength = maxLength > -1 && len > maxLength

    const isValid =
      !regExViolation && !isMissingRequiredValue && !exceedsMaxLength

    if (isValid !== this.state.isValid) {
      onValidityChange(isValid, event.target.name)
    }

    let newState = {
      isValid,
      regExViolation,
      isMissingRequiredValue,
    }

    if (this.props.maxLength > -1) {
      newState = Object.assign({}, newState, { charsRemaining })
    }

    this.setState(newState)

    const newEvent = this.setEventMetadata(event)
    this.props.onChange(newEvent)
  }

  handleBlur = (event) => {
    const newEvent = this.setEventMetadata(event)
    this.props.onBlur(newEvent)
  }

  handleFocus = (event) => {
    const newEvent = this.setEventMetadata(event)
    this.props.onFocus(newEvent)
  }

  setEventMetadata = (event) => {
    event.target.name = this.props.name === '' ? 'InputBox' : this.props.name
    return event
  }

  render() {
    const {
      placeholder,
      width,
      className,
      value,
      disabled,
      rows,
      maxLength,
      autosize,
      type,
      suffix,
      disallowedCharacters,
      size,
      style,
      addonAfter,
      addonBefore,
      defaultValue,
      id,
      prefix,
      enterButton,
      onSearch,
      onKeyPress,
      name,
      label,
      vertical,
      box,
      charsRemainingNumberOnly,
      customErrorMessage,
      required,
      hint,
      tooltip,
      hideCounter,
    } = this.props

    const {
      isValid,
      disallowedRegEx,
      charsRemaining,
      regExViolation,
      isMissingRequiredValue,
    } = this.state

    return (
      <div
        className={classNames('form-field', 'input-box', {
          box,
          vertical,
        })}
        style={width ? { width } : {}}
      >
        {label && (
          <InputLabel
            label={label}
            hint={hint}
            tooltip={tooltip}
            required={required}
            size={size}
            vertical={vertical}
          />
        )}
        {type === 'textarea' && (
          <Input.TextArea
            placeholder={placeholder}
            onBlur={this.handleBlur}
            onFocus={this.handleFocus}
            onChange={this.handleChange}
            className={classNames(className, {
              'has-error': !isValid || customErrorMessage,
            })}
            defaultValue={defaultValue}
            disabled={disabled}
            rows={rows}
            autosize={autosize}
            value={value}
          />
        )}
        {type === 'search' && (
          <Input.Search
            style={style}
            className={classNames(className, {
              'has-error': !isValid || customErrorMessage,
            })}
            placeholder={placeholder}
            addonBefore={addonBefore}
            addonAfter={addonAfter}
            defaultValue={defaultValue}
            disabled={disabled}
            id={id}
            prefix={prefix}
            suffix={suffix}
            size={size}
            value={value}
            onChange={this.handleChange}
            maxLength={maxLength > -1 ? maxLength : undefined}
            enterButton={enterButton}
            onSearch={onSearch}
            onKeyPress={onKeyPress}
          />
        )}
        {!['textarea', 'search'].includes(type) && (
          <Input
            style={style}
            onKeyPress={onKeyPress}
            addonBefore={addonBefore}
            addonAfter={addonAfter}
            id={id}
            prefix={prefix}
            suffix={suffix}
            value={value}
            placeholder={placeholder}
            onBlur={this.handleBlur}
            onFocus={this.handleFocus}
            onChange={this.handleChange}
            className={classNames(className, {
              'has-error': !isValid || customErrorMessage,
            })}
            size={size}
            defaultValue={defaultValue}
            disabled={disabled}
            autoComplete="off"
          />
        )}
        {/* Display a footnote if appropriate. Violations are evaluated and displayed in
                order of precedence. Only one error footnote is displayed at a time. */}
        {!hideCounter &&
          (!isValid || maxLength > -1 || isMissingRequiredValue) && (
            <div className={'text-right'}>
              {(regExViolation && disallowedRegEx && (
                <span className="input-box_error">
                  {strings.disallowed_chars(disallowedCharacters)}
                </span>
              )) ||
                (maxLength > -1 && (
                  <span className={charsRemaining < 0 ? 'input-box_error' : ''}>
                    {charsRemainingNumberOnly
                      ? charsRemaining
                      : strings.chars_remaining(charsRemaining)}
                  </span>
                )) ||
                (isMissingRequiredValue && (
                  <span className="input-box_error">
                    {strings.missing_required_value(name)}
                  </span>
                ))}
            </div>
          )}
        {!customErrorMessage && required && this.state.isDirty && !value && (
          <span className="error-msg">{strings.selection_required}</span>
        )}
        {customErrorMessage && (
          <span className="error-msg">{customErrorMessage}</span>
        )}
      </div>
    )
  }
}

InputBox.defaultProps = {
  placeholder: '',
  value: undefined,
  defaultValue: undefined, // Must not define both defaultValue and value.
  className: '',
  type: 'default',
  size: 'default',
  name: '',
  maxLength: -1,
  vertical: false,
  box: false,
  onBlur: () => {},
  onFocus: () => {},
  onChange: () => {},
  onValidityChange: () => {},
  hideCounter: false,
}

InputBox.propTypes = {
  id: PropTypes.string,
  defaultValue: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  className: PropTypes.string,
  name: PropTypes.string,
  customErrorMessage: PropTypes.string,

  // Array of characters. If any of the disallowedCharacters are entered, the InputBox enters an invalid state. Optional.
  disallowedCharacters: PropTypes.arrayOf(PropTypes.string),

  // currently, the only recognized type value is 'textarea', which invokes a text area rather than the default text box.
  type: PropTypes.oneOf(['textarea', 'search', 'default']),
  // May be set to 'small' or 'large' to be smaller or larger than default. Optional.
  size: PropTypes.oneOf(['small', 'default', 'large']),

  // called anytime the validity changes. Once called, will not be called again until validity changes again. Optional.
  onValidityChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onChange: PropTypes.func,
  onSearch: PropTypes.func,
  onKeyPress: PropTypes.func,

  addonBefore: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  addonAfter: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  prefix: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  suffix: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

  // For text area inputs. Object with structure of: { minRows: 2, maxRows: 6 }
  autosize: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  style: PropTypes.object,

  // Explicitly set the width. Optional.
  width: PropTypes.number,
  rows: PropTypes.number,
  // Input will allow indefinite string length but when maxLength is set, will show a countdown of characters remaining (or exceeded).
  // Validity changes to false when maxLength is set to a positive value and exceeded by the length of the input. Optional.
  maxLength: PropTypes.number,
  required: PropTypes.bool,

  enterButton: PropTypes.bool,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  vertical: PropTypes.bool,
  box: PropTypes.bool,

  // Only show characters remaining number
  charsRemainingNumberOnly: PropTypes.bool,

  // Suppress the characters-remaining / validation footer entirely (e.g. search inputs)
  hideCounter: PropTypes.bool,
}
